/**
 * The money beside a vote, and the four things it is not.
 *
 * A registrant owes two forms. On the quarterly LD-2 they name the bills
 * they lobbied on; on the semi-annual LD-203 they name the members they gave
 * money to. Where both point at the same roll, this shows it — and the whole
 * difficulty of the feature is that it must not be read as a transaction,
 * because no filing reports one.
 *
 * So the wording here is load-bearing rather than decorative, and three
 * choices in it are deliberate.
 *
 * The cell says "from 3 who lobbied it" and never "3 donors on this vote".
 * A screenshot of one row travels without the page around it, so the
 * qualifier has to be inside the cell rather than only above the list.
 *
 * The note is always rendered, including for a member with nothing beside
 * any roll. Only about a fifth of lobbying reports name a bill by number, so
 * a blank column means nobody filed that bill's number — and the reader most
 * likely to take absence for innocence is the one looking at a blank column.
 *
 * Nothing anywhere in here says which way the member voted relative to the
 * organisations listed. An LD-2 discloses the bill and never whether the
 * filer wanted it passed or killed, so that sentence cannot be written from
 * this record at all.
 */

import { Link } from "react-router-dom";
import { api, type BillLobbyingCoverage, type VoteMoneyDetail, type VoteMoneySummary } from "../api";
import { useAsync } from "../useAsync";
import { Problem } from "./bits";

/** Exact, with commas, the way the donor list on the same page does it.
 *  "$3.1m" is easier to scan and loses the difference between two rolls
 *  that a reader is here to compare. */
function dollars(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

function cheques(n: number): string {
  return n === 1 ? "1 cheque" : `${n.toLocaleString()} cheques`;
}

/**
 * One roll's cell in the voting record.
 *
 * Nothing at all when nobody filed, rather than "$0". Nine rolls in ten are
 * empty and a measured-looking zero on all of them says the record was
 * checked and came back clean, which is the one thing it cannot say.
 */
export function VoteMoneyCell({ found }: { found: VoteMoneySummary }) {
  if (!found.donors) return null;
  const onlyAfter = found.before === 0 && found.after > 0;

  return (
    <span className="shrink-0 text-right">
      <span className="tnum block text-sm font-semibold">{dollars(found.amount)}</span>
      <span className="block text-[11px] leading-tight text-faint">
        from {found.donors.toLocaleString()} who lobbied it
      </span>
      {/* The most interesting row on the page. Money disclosed after a vote
          cannot have influenced it, and a cell that rendered it the same as
          money disclosed before would flatten the one distinction here that
          settles anything. */}
      {onlyAfter && (
        <span className="block text-[11px] leading-tight text-amber-700">
          all of it after the vote
        </span>
      )}
    </span>
  );
}

/**
 * The caveat, on the page rather than in a tooltip.
 *
 * Every sentence here is answering a specific way this feature can be
 * misread, and the two figures in it are measured rather than written down:
 * the crawl is still filling the bill table, so a hard-coded share would
 * make the page's own caveat the stalest thing on it.
 */
export function VoteMoneyNote({
  coverage,
  found,
}: {
  coverage: BillLobbyingCoverage;
  found: number;
}) {
  const share = coverage.share === null ? null : Math.round(coverage.share * 100);

  return (
    <div className="mt-3 rounded-2xl border border-line bg-sunk/40 p-4 text-xs leading-relaxed text-muted">
      {found > 0 ? (
        <p>
          Where a roll carries a figure, organisations that disclosed lobbying that bill also
          disclosed giving this member money. What that is, and what it is not:
        </p>
      ) : (
        <p>
          No organisation that disclosed lobbying one of these bills appears on this member's
          disclosed donor list. Before reading anything into that:
        </p>
      )}
      <ul className="mt-2 space-y-2">
        <li>
          <span className="font-medium text-ink">Two separate filings.</span> Lobbying is
          disclosed quarterly on an LD-2; the money is disclosed twice a year on an LD-203.
          Neither form refers to the other, and no filing reports money given for a bill. So
          this is two disclosures that happen to name the same bill — worth seeing, and{" "}
          <span className="font-medium text-ink">not evidence</span> that money bought a vote.
        </li>
        <li>
          <span className="font-medium text-ink">The record never shows which side they took.</span>{" "}
          A lobbying report names the bill and never whether the filer wanted it passed or
          killed. Nothing here says a member voted with or against the organisations that funded
          them, because the record cannot say it.
        </li>
        <li>
          <span className="font-medium text-ink">An empty row is not a clean one.</span>{" "}
          {coverage.named.toLocaleString()} of {coverage.activities.toLocaleString()} lobbying
          reports name a bill by number
          {share === null ? "" : ` — ${share}%`}; the rest describe their subject in words that
          name none. Nothing beside a roll means{" "}
          <span className="font-medium text-ink">nobody filed that bill's number</span>, not that
          nobody lobbied it.
        </li>
        <li>
          <span className="font-medium text-ink">Each figure answers one roll.</span> It is
          everything those organisations have disclosed giving this member, not money given for
          that bill, and the same cheques answer every roll on the same bill. This is not a
          column to total.
        </li>
      </ul>
    </div>
  );
}

/** The sheet behind one roll, fetched when a reader opens it. */
export function VoteMoneyPanel({ bioguide, voteId }: { bioguide: string; voteId: string }) {
  const found = useAsync(() => api.voteMoney(bioguide, voteId), [bioguide, voteId]);
  if (found.error) return <Problem error={found.error} retry={found.reload} />;
  if (!found.data) {
    return <p className="px-4 pb-4 text-xs text-faint">Reading both filings…</p>;
  }
  return <VoteMoneySheet detail={found.data} />;
}

/**
 * One roll in full: who filed both halves, and when the money moved.
 *
 * `registrants` leads the paragraph on purpose. Two organisations out of
 * forty-seven is a different fact from two out of two, and the funders
 * listed without the field they were drawn from read as though funding is
 * what lobbying looks like.
 */
export function VoteMoneySheet({ detail }: { detail: VoteMoneyDetail }) {
  const named = detail.display_name ?? detail.bill_id ?? "this bill";
  const shown = detail.donors.length;
  // The list is capped and the count is not. Written from the list length,
  // this said "25 of the 147" beneath a cell that said 30 -- the page
  // disagreeing with itself about the only figure it exists to report.
  const funders = shown + detail.hidden;

  return (
    <div className="border-t border-line bg-sunk/30 px-4 py-4">
      <p className="text-sm">
        {funders === 0 ? (
          <>
            None of the {detail.registrants.toLocaleString()} organisations that disclosed
            lobbying {named} appear on this member's disclosed donor list.
          </>
        ) : (
          <>
            <span className="font-medium">
              {funders.toLocaleString()} of the {detail.registrants.toLocaleString()} organisations
            </span>{" "}
            that disclosed lobbying {named} also disclosed giving this member money. Neither
            filing refers to the other.
          </>
        )}
      </p>

      {funders > 0 && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Half
              label={detail.date ? `Before the vote on ${detail.date}` : "Before the vote"}
              amount={detail.before}
              gifts={detail.gifts_before}
            />
            <Half
              label="On the day of the vote, or since"
              amount={detail.after}
              gifts={detail.gifts_after}
            />
          </div>

          <p className="mt-2 text-xs leading-relaxed text-muted">
            Money disclosed after a vote{" "}
            <span className="font-medium text-ink">cannot have influenced</span> it, which is the
            only thing on this page that settles anything. A cheque dated the same day as the roll
            is counted with the money that came after, because neither form carries a time and
            nothing shows it arrived first.
          </p>

          {/* The roll with no published date. Shown rather than folded into
              one of the halves, which would claim a placement the record
              does not support, and rather than dropped, which would leave
              the halves adding to less than the total. */}
          {detail.unplaced > 0 && (
            <p className="mt-2 text-xs leading-relaxed text-muted">
              The chamber published no date for this roll, so {dollars(detail.unplaced)} of it
              cannot be placed before or after the vote.
            </p>
          )}

          <ul className="mt-4 divide-y divide-line overflow-hidden rounded-xl border border-line bg-card">
            {detail.donors.map((donor) => (
              <li key={donor.registrant_id} className="px-3 py-2.5">
                <div className="flex items-baseline gap-3">
                  <Link
                    to={`/money/${donor.registrant_id}`}
                    className="min-w-0 flex-1 truncate text-sm font-medium text-brand hover:underline"
                    title={donor.name}
                  >
                    {donor.name.toLowerCase()}
                  </Link>
                  <span className="tnum shrink-0 text-sm font-medium">
                    {dollars(donor.amount)}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] leading-tight text-faint">
                  {/* The client, which is regularly not the registrant: a
                      law firm lobbies on behalf of somebody, and the
                      somebody is whose interest it was. */}
                  {donor.clients.length > 0 && <>for {donor.clients.join(", ").toLowerCase()} · </>}
                  {donor.activities === 1 ? "1 filing" : `${donor.activities} filings`}
                  {donor.first_year !== null && (
                    <>
                      ,{" "}
                      {donor.first_year === donor.last_year
                        ? donor.first_year
                        : `${donor.first_year}–${donor.last_year}`}
                    </>
                  )}
                </p>
                <p className="tnum mt-0.5 text-[11px] leading-tight text-muted">
                  {dollars(donor.before)} before · {dollars(donor.after)} on the day or since
                </p>
              </li>
            ))}
          </ul>

          {detail.hidden > 0 && (
            <p className="mt-2 text-xs text-faint">
              A further {detail.hidden.toLocaleString()} organisations gave{" "}
              {dollars(detail.hidden_amount)} and are not listed. The figures above include them.
            </p>
          )}

          <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-muted">
            Each amount is everything that organisation has disclosed giving this member across the
            whole LD-203 record —{" "}
            <span className="font-medium text-ink">not money given for this bill</span>. No filing
            reports money given for a bill, so there is no such figure to show.
          </p>
        </>
      )}
    </div>
  );
}

function Half({ label, amount, gifts }: { label: string; amount: number; gifts: number }) {
  return (
    <div className="rounded-xl border border-line bg-card p-3">
      <p className="text-xs text-muted">{label}</p>
      <p className="tnum mt-1 font-display text-xl font-semibold">{dollars(amount)}</p>
      <p className="text-xs text-faint">{cheques(gifts)}</p>
    </div>
  );
}
